// src/hooks/useStoreManagement.js
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../AuthContext';
import useUserProfile from './useUserProfile';
import { 
  getAllStores, 
  getStoresByType, 
  getStoresByManager, 
  getStore,
  canManageStore,
  STORE_TYPES,
  STORE_MANAGER_ROLES,
  isStoreManager,
  isStoreManagerOrAdmin
} from '../lib/storeManagement';

export default function useStoreManagement() {
  const { user } = useAuth();
  const { profile } = useUserProfile();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Store management state
  const [stores, setStores] = useState([]);
  const [managedStores, setManagedStores] = useState([]);
  const [currentStore, setCurrentStore] = useState(null);

  // Check if user is a store manager or admin
  const isUserStoreManager = isStoreManager(profile?.roles);
  const isUserAdmin = profile?.roles?.includes('admin');
  const isUserStoreManagerOrAdmin = isStoreManagerOrAdmin(profile?.roles);

  // Get user's store manager role
  const storeManagerRole = profile?.roles?.find(role => 
    Object.values(STORE_MANAGER_ROLES).includes(role)
  );

  // Get store type from user's role
  const userStoreType = storeManagerRole ? 
    Object.keys(STORE_MANAGER_ROLES).find(key => 
      STORE_MANAGER_ROLES[key] === storeManagerRole
    )?.replace('_STORE_MANAGER', '').toLowerCase() : null;

  // Load all stores
  const loadAllStores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await getAllStores();
      if (result.success) {
        setStores(result.stores);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load stores by type
  const loadStoresByType = useCallback(async (storeType) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getStoresByType(storeType);
      if (result.success) {
        setStores(result.stores);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Load stores managed by current user
  const loadManagedStores = useCallback(async () => {
    if (!user?.uid) return;
    
    setLoading(true);
    setError(null);
    try {
      const result = await getStoresByManager(user.uid);
      if (result.success) {
        setManagedStores(result.stores);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [user?.uid]);

  // Load specific store
  const loadStore = useCallback(async (storeId) => {
    setLoading(true);
    setError(null);
    try {
      const result = await getStore(storeId);
      if (result.success) {
        setCurrentStore(result.store);
      } else {
        setError(result.error);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Check if user can manage a specific store
  const checkStoreManagementPermission = useCallback(async (storeId) => {
    if (!user?.uid || !profile?.roles) {
      return { canManage: false, reason: 'not_authenticated' };
    }

    try {
      const result = await canManageStore(user.uid, profile.roles, storeId);
      return result;
    } catch (err) {
      console.error('Error checking store management permission:', err);
      return { canManage: false, reason: 'error_checking_permissions' };
    }
  }, [user?.uid, profile?.roles]);

  // Auto-load managed stores when user is a store manager or admin
  useEffect(() => {
    if (isUserStoreManagerOrAdmin && user?.uid) {
      if (isUserAdmin) {
        // Admin sees all stores
        loadAllStores();
      } else {
        // Store manager sees only their managed stores
        loadManagedStores();
      }
    }
  }, [isUserStoreManagerOrAdmin, isUserAdmin, user?.uid]);

  return {
    // State
    stores,
    managedStores,
    currentStore,
    loading,
    error,
    
    // User info
    isUserStoreManager,
    isUserAdmin,
    isUserStoreManagerOrAdmin,
    storeManagerRole,
    userStoreType,
    
    // Actions
    loadAllStores,
    loadStoresByType,
    loadManagedStores,
    loadStore,
    checkStoreManagementPermission,
    
    // Utilities
    setError,
    clearError: useCallback(() => setError(null), [])
  };
}
